import { useMyMembership } from '@/shared/hooks/useTeam';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { useAuthStore } from '@/shared/stores/auth.store';
import type { TeamRole } from '@/shared/services/team.service';

export interface MyRole {
  /** The signed-in person's role on the brand in view; null until known. */
  role: TeamRole | null;
  isLoading: boolean;
}

/**
 * The signed-in person's role on the current brand. The owner is known at
 * once from the brand row; anyone else from GET /team/me, which every role
 * may read (the team list itself is for owners, admins and editors).
 */
export function useMyRole(): MyRole {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const ownerId = useWorkspaceStore((s) => s.currentWorkspace?.ownerId ?? null);
  const isOwner = !!userId && ownerId === userId;
  const { data: me, isLoading } = useMyMembership();
  if (isOwner) return { role: 'owner', isLoading: false };
  return { role: me?.role ?? null, isLoading: isLoading && !me };
}

/**
 * Who may do what with billing (ADR-006, the API's role rules in the
 * billing contract): the account owner and a BILLING_ADMIN pay, confirm and
 * read invoices; the owner, a BILLING_ADMIN and an ADMIN read the billing
 * details, and only the account owner saves them (PUT /billing/identity is
 * owner-only).
 */
export function billingRights(role: TeamRole | null): { canPay: boolean; canReadIdentity: boolean; canSaveIdentity: boolean } {
  const canPay = role === 'owner' || role === 'billing_admin';
  return {
    canPay,
    canReadIdentity: canPay || role === 'admin',
    canSaveIdentity: role === 'owner',
  };
}
